import { useCallback, useEffect, useRef, useState } from 'react'
import { useEditorStore } from '../../../store/editorStore'
import { clampPxPerSec, fitPxPerSec, getTotalDurationSec, pxToSec, ZOOM_STEP_FACTOR } from '../../../lib/timelineMath'
import { Ruler } from './Ruler'
import { VideoLane } from './VideoLane'
import { OverlayLane } from './OverlayLane'
import { AudioLane } from './AudioLane'
import { Playhead, SnapGuide } from './Playhead'
import { Toolbar } from './Toolbar'
import {
  AUDIO_LANE_H,
  OVERLAY_LANE_ORDER,
  OVERLAY_SUBLANE_H,
  RULER_H,
  TRACK_HEADER_W,
  VIDEO_LANE_H,
  overlayLaneHeight,
  overlayLanesTotalHeight,
} from './constants'

/**
 * task spec §5 (相互依存): Timeline のルートは親から与えられた高さに `h-full` で
 * 追従する。1b の EditorScreen は実際には
 * `<div style={{ height: panelSizes.timeline }} className="shrink-0 overflow-hidden">
 *    <Timeline />
 *  </div>`
 * という「明示 px 高さを持つ普通の block 要素」でラップしている（EditorScreen.tsx
 * のコメント参照）。このラッパーは flex コンテナではなく単なる block 要素なので、
 * 中の Timeline に `h-full`（height:100%）を与えても曖昧さなくラッパーの高さに
 * 解決される — flex アイテムに直接 %指定するときの「兄弟を潰して画面を占領する」
 * 定番の罠（h-screen 直下の flex-col に他の兄弟と並べて %指定を置いた場合に
 * 起こる）はここでは踏まない。念のため、ラッパーの無い/height 未確定な状況
 * （孤立した描画・将来の変更）向けに `min-h-[260px]` を下限として残し、呼び出し
 * 側が明示 `height` prop を渡した場合はそれを inline style で優先する。
 */
export function Timeline({ height }: { height?: number } = {}) {
  const composition = useEditorStore((s) => s.composition)
  const assets = useEditorStore((s) => s.assets)
  const currentSec = useEditorStore((s) => s.playback.currentSec)
  const setPlayhead = useEditorStore((s) => s.setPlayhead)
  const select = useEditorStore((s) => s.select)
  const zoomPxPerSec = useEditorStore((s) => s.zoomPxPerSec)
  const setZoomPxPerSec = useEditorStore((s) => s.setZoomPxPerSec)
  const setEffectivePxPerSec = useEditorStore((s) => s.setEffectivePxPerSec)
  const snapGuideSec = useEditorStore((s) => s.snapGuideSec)
  const timelineNotice = useEditorStore((s) => s.timelineNotice)

  // UI_MK2_PHASE2.md §2-7 (reviewer minor): この関数は composition が null の
  // 間 `return null` する（下記）ので、初回コミットでは `data-testid="timeline-
  // scroll"` の実 DOM が存在しない。旧実装は `useRef<HTMLDivElement>(null)` +
  // 空配列 `useEffect` だったため、その 1 回きりの effect 実行時に
  // `scrollRef.current` が null のまま — その後 composition が届いて実要素が
  // マウントされても、依存配列が空のこの effect は二度と再実行されず
  // ResizeObserver/wheel リスナーが永遠に付かない（潜在バグ、ズーム計算・
  // ⌘/Ctrl+ホイールが動かないまま気づかれない）。callback ref で実際に要素が
  // アタッチされた瞬間を state（`scrollEl`）に反映し、それを依存配列に使う
  // ことで「要素が後から現れる」パターンでも確実に発火させる。
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null)
  const attachScrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node
    setScrollEl(node)
  }, [])
  const [containerWidth, setContainerWidth] = useState(800)

  useEffect(() => {
    if (!scrollEl) return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w) setContainerWidth(w)
    })
    ro.observe(scrollEl)
    return () => ro.disconnect()
  }, [scrollEl])

  const totalSec = composition ? getTotalDurationSec(composition) : 0
  // task spec §2-2: 既定「全体を表示」= zoomPxPerSec が null の間は毎レンダー
  // containerWidth に合わせてフィットし直す。明示ズーム操作をすると数値が入る。
  const pxPerSec = zoomPxPerSec ?? fitPxPerSec(totalSec, containerWidth)

  useEffect(() => {
    setEffectivePxPerSec(pxPerSec)
  }, [pxPerSec, setEffectivePxPerSec])

  // reviewer 差し戻し M2: React の JSX onWheel はルートに passive:true で登録
  // されるため、その中で e.preventDefault() を呼んでも効かず（"Unable to
  // preventDefault inside passive event listener" が console に出る）、
  // ⌘/Ctrl+ホイールでのズームや Shift+ホイールの横スクロールがブラウザの既定
  // 挙動（ページスクロール等）に食われる。addEventListener に
  // { passive: false } で明示的に登録し直す。pxPerSec は ref で最新値を渡し、
  // このエフェクト自体は mount 時に一度だけ登録（store のアクションは zustand
  // が安定参照を返すので依存配列に入れても再登録は事実上起きない）。
  const pxPerSecRef = useRef(pxPerSec)
  useEffect(() => {
    pxPerSecRef.current = pxPerSec
  }, [pxPerSec])

  useEffect(() => {
    const el = scrollEl
    if (!el) return
    const handleWheel = (e: WheelEvent) => {
      const currentPxPerSec = pxPerSecRef.current
      // task spec §2-2: ⌘/Ctrl+ホイールでカーソル位置を中心にズーム
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault()
        const rect = el.getBoundingClientRect()
        const cursorXInContent = e.clientX - rect.left + el.scrollLeft
        const cursorSec = pxToSec(cursorXInContent, currentPxPerSec)
        const factor = e.deltaY < 0 ? ZOOM_STEP_FACTOR : 1 / ZOOM_STEP_FACTOR
        const nextPxPerSec = clampPxPerSec(currentPxPerSec * factor)
        setZoomPxPerSec(nextPxPerSec)
        const cursorXInViewport = e.clientX - rect.left
        requestAnimationFrame(() => {
          el.scrollLeft = Math.max(0, cursorSec * nextPxPerSec - cursorXInViewport)
        })
        return
      }
      // task spec §2-2: Shift+ホイールで横スクロール
      if (e.shiftKey) {
        e.preventDefault()
        el.scrollLeft += e.deltaY !== 0 ? e.deltaY : e.deltaX
      }
    }
    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [scrollEl, setZoomPxPerSec])

  if (!composition) return null

  const overlays = composition.tracks.overlays
  const contentWidth = Math.max(containerWidth, (totalSec + 5) * pxPerSec)
  const contentHeight = RULER_H + VIDEO_LANE_H + overlayLanesTotalHeight(overlays) + AUDIO_LANE_H

  function scrubFromClientX(clientX: number) {
    const rect = scrollRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = clientX - rect.left + (scrollRef.current?.scrollLeft ?? 0)
    setPlayhead(Math.max(0, pxToSec(x, pxPerSec)))
  }

  return (
    <div
      className="border-t border-ink-border bg-ink-surface/60 flex flex-col h-full min-h-[260px] relative"
      style={height != null ? { height } : undefined}
      data-testid="timeline"
    >
      <Toolbar />
      <div className="flex flex-1 min-h-0">
        {/* task spec §2-2: トラックヘッダーは横スクロールしても固定（スクロール
            コンテナの外側に置くだけで達成される） */}
        <div
          className="shrink-0 border-r border-ink-border flex flex-col text-[10px] text-text-faint"
          style={{ width: TRACK_HEADER_W }}
        >
          <div style={{ height: RULER_H }} className="flex items-center justify-center border-b border-ink-border/40">
            時間
          </div>
          <div
            style={{ height: VIDEO_LANE_H }}
            className="flex items-center justify-center border-b border-ink-border/40"
          >
            映像
          </div>
          {OVERLAY_LANE_ORDER.map((lane) => {
            const h = overlayLaneHeight(overlays, lane.key)
            return (
              <div
                key={lane.key}
                style={{ height: h }}
                className="flex items-center justify-center border-b border-ink-border/30 text-center leading-tight px-0.5 overflow-hidden"
              >
                {h >= OVERLAY_SUBLANE_H ? lane.label : ''}
              </div>
            )
          })}
          <div style={{ height: AUDIO_LANE_H }} className="flex items-center justify-center">
            音声
          </div>
        </div>

        <div
          ref={attachScrollRef}
          className="flex-1 overflow-x-auto overflow-y-hidden relative"
          data-testid="timeline-scroll"
        >
          <div
            style={{ width: contentWidth, height: contentHeight }}
            className="relative"
            onClick={(e) => {
              // 各レーンは自前で空白クリックを拾うので、ここまで来るのは
              // ルーラー右側の余白などの本当の「何もない場所」クリック。
              if (e.target === e.currentTarget) select(null)
            }}
          >
            <Ruler totalSec={totalSec} pxPerSec={pxPerSec} onScrub={scrubFromClientX} />
            <VideoLane composition={composition} assets={assets} pxPerSec={pxPerSec} />
            <OverlayLane composition={composition} pxPerSec={pxPerSec} />
            <AudioLane composition={composition} assets={assets} pxPerSec={pxPerSec} />
            <Playhead currentSec={currentSec} totalSec={totalSec} pxPerSec={pxPerSec} />
            <SnapGuide sec={snapGuideSec} pxPerSec={pxPerSec} />
          </div>
        </div>
      </div>
      {timelineNotice && (
        <div
          className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-ink-surface2 border border-ink-border text-text text-[11px] px-3 py-1.5 rounded shadow-glass z-40"
          data-testid="tl-notice"
        >
          {timelineNotice}
        </div>
      )}
    </div>
  )
}
